/**
 * Adapted from PlayCanvas Engine's scripts/esm/gsplat/gsplat-image.mjs at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */

import * as THREE from 'three'
import type { GSplatResourceLease } from '@/plugins/gsplat/GSplatHandleBinding'
import type { GSplatGraphicsAdapter } from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import { GSplatFormat } from '@/plugins/gsplat/lod/GSplatFormat'
import type {
  GaussianSplatHandle,
  GaussianSplatLoadRequest,
} from '@/plugins/gsplat/types/PluginTypes'
import { GSplatContainer } from '@/plugins/gsplat/unified/resource/GSplatContainer'
import { getLoggerManager } from '@/shared/logging'
import type { AssetId, Disposable } from '@/shared/types/core'

const log = getLoggerManager().getLogger('gsplat:image')

export interface GSplatImageTexture {
  readonly width: number
  readonly height: number
  readonly revision?: unknown
  getSource(): CanvasImageSource | null
}

/** 与 PlayCanvas texture Asset 对齐的最小输入边界。 */
export interface GSplatImageAsset {
  readonly id?: unknown
  readonly revision?: unknown
  readonly resource: GSplatImageTexture | null
}

export interface GSplatImageCanvasContext {
  drawImage(source: CanvasImageSource, dx: number, dy: number): void
  getImageData(sx: number, sy: number, sw: number, sh: number): ImageData
}

export interface GSplatImageCanvas {
  width: number
  height: number
  getContext(contextId: '2d'): GSplatImageCanvasContext | null
}

export interface GSplatImageRuntime {
  readonly graphicsAdapter: GSplatGraphicsAdapter
  createWithResource(
    request: Readonly<GaussianSplatLoadRequest>,
    resource: GSplatResourceLease
  ): GaussianSplatHandle
  replaceWithResource(
    id: AssetId,
    resource: GSplatResourceLease,
    metadata?: { readonly url?: string; readonly name?: string }
  ): GaussianSplatHandle
  mount(handle: GaussianSplatHandle): void
  disposeAsset(id: AssetId): void
}

export interface GSplatImageOptions {
  readonly id?: AssetId
  readonly name?: string
  readonly imageAsset?: GSplatImageAsset | null
  readonly canvasFactory?: () => GSplatImageCanvas
}

export type GSplatImageErrorCode =
  | 'GSPLAT_IMAGE_DISPOSED'
  | 'GSPLAT_IMAGE_INVALID_DIMENSIONS'
  | 'GSPLAT_IMAGE_SOURCE_UNAVAILABLE'
  | 'GSPLAT_IMAGE_CANVAS_UNAVAILABLE'
  | 'GSPLAT_IMAGE_CONTEXT_UNAVAILABLE'
  | 'GSPLAT_IMAGE_READBACK_FAILED'
  | 'GSPLAT_IMAGE_ALLOCATION_FAILED'
  | 'GSPLAT_IMAGE_UPLOAD_FAILED'
  | 'GSPLAT_IMAGE_UPDATE_FAILED'
  | 'GSPLAT_IMAGE_COMMIT_FAILED'
  | 'GSPLAT_IMAGE_STALE'

export class GSplatImageError extends Error {
  constructor(
    readonly code: GSplatImageErrorCode,
    message: string,
    readonly cause?: unknown
  ) {
    super(message)
    this.name = 'GSplatImageError'
  }
}

interface GSplatImageSourceKey {
  readonly assetIdentity: unknown
  readonly assetRevision: unknown
  readonly textureIdentity: GSplatImageTexture | null
  readonly textureRevision: unknown
}

function defaultCanvasFactory(): GSplatImageCanvas {
  if (typeof document === 'undefined') {
    throw new GSplatImageError(
      'GSPLAT_IMAGE_CANVAS_UNAVAILABLE',
      'GSplatImage 当前环境不能创建 canvas'
    )
  }
  return document.createElement('canvas')
}

function sourceKeyEquals(left: GSplatImageSourceKey, right: GSplatImageSourceKey): boolean {
  return (
    Object.is(left.assetIdentity, right.assetIdentity) &&
    Object.is(left.assetRevision, right.assetRevision) &&
    left.textureIdentity === right.textureIdentity &&
    Object.is(left.textureRevision, right.textureRevision)
  )
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

/** FrameDriver Update phase 驱动的 backend-neutral 图片 GSplat generator。 */
export class GSplatImage implements Disposable {
  imageAsset: GSplatImageAsset | null

  private readonly id?: AssetId
  private readonly name: string
  private readonly canvasFactory: () => GSplatImageCanvas
  private formatValue: GSplatFormat | null = null
  private handleValue: GaussianSplatHandle | null = null
  private stagedHandle: GaussianSplatHandle | null = null
  private stagedContainer: GSplatContainer | null = null
  private committedSourceKey: GSplatImageSourceKey | null = null
  private hasCommittedSourceKey = false
  private generation = 0
  private initialized = false
  private disposed = false
  private errorValue: GSplatImageError | null = null

  constructor(
    private readonly runtime: GSplatImageRuntime,
    options: GSplatImageOptions = {},
    private readonly onDispose?: (generator: GSplatImage) => void
  ) {
    this.id = options.id
    this.name = options.name ?? 'GSplatImage'
    this.imageAsset = options.imageAsset ?? null
    this.canvasFactory = options.canvasFactory ?? defaultCanvasFactory
  }

  get handle(): GaussianSplatHandle | null {
    return this.handleValue
  }

  get format(): GSplatFormat | null {
    return this.formatValue
  }

  get lastError(): GSplatImageError | null {
    return this.errorValue
  }

  /** 对齐上游 initialize；本地只建立 generator binding，不提前分配空 GPU container。 */
  initialize(): void {
    this.assertActive()
    if (this.initialized) return
    this.initialized = true
  }

  /** 对齐上游 postUpdate；相同 source identity/revision 不重复读回和上传。 */
  postUpdate(): void {
    this.assertActive()
    if (!this.initialized) this.initialize()
    const sourceKey = this.getSourceKey()
    if (
      this.hasCommittedSourceKey &&
      this.committedSourceKey &&
      sourceKeyEquals(this.committedSourceKey, sourceKey)
    ) {
      return
    }

    try {
      this._rebuildFromImage()
      this.committedSourceKey = sourceKey
      this.hasCommittedSourceKey = true
      this.errorValue = null
    } catch (error) {
      const structured = this.toError(error, 'GSPLAT_IMAGE_COMMIT_FAILED', '图片重建失败')
      this.errorValue = structured
      log.warn(`GSplatImage 重建失败: code=${structured.code}, reason=${structured.message}`)
    }
  }

  /**
   * 对齐上游 _rebuildFromImage；所有 GPU 叶子写入完成后才提交 stable Handle。
   */
  _rebuildFromImage(): GaussianSplatHandle | null {
    this.assertActive()
    if (!this.initialized) this.initialize()
    const operationGeneration = ++this.generation
    const texture = this.imageAsset?.resource ?? null
    if (!texture) {
      this.commitEmpty(operationGeneration)
      return null
    }

    const { width, height } = texture
    if (
      !Number.isSafeInteger(width) ||
      !Number.isSafeInteger(height) ||
      width <= 0 ||
      height <= 0 ||
      width * height > Number.MAX_SAFE_INTEGER / 4
    ) {
      throw new GSplatImageError(
        'GSPLAT_IMAGE_INVALID_DIMENSIONS',
        `GSplatImage 图片尺寸非法: ${width}x${height}`
      )
    }

    let source: CanvasImageSource | null
    try {
      source = texture.getSource()
    } catch (error) {
      throw new GSplatImageError(
        'GSPLAT_IMAGE_SOURCE_UNAVAILABLE',
        `GSplatImage 获取纹理源失败: ${errorMessage(error)}`,
        error
      )
    }
    if (!source) {
      throw new GSplatImageError(
        'GSPLAT_IMAGE_SOURCE_UNAVAILABLE',
        'GSplatImage 纹理没有可读 source'
      )
    }

    let canvas: GSplatImageCanvas
    try {
      canvas = this.canvasFactory()
      canvas.width = width
      canvas.height = height
    } catch (error) {
      throw this.toError(error, 'GSPLAT_IMAGE_CANVAS_UNAVAILABLE', '创建 canvas 失败')
    }
    let context: GSplatImageCanvasContext | null
    try {
      context = canvas.getContext('2d')
    } catch (error) {
      throw new GSplatImageError(
        'GSPLAT_IMAGE_CONTEXT_UNAVAILABLE',
        `GSplatImage 创建 2D canvas context 失败: ${errorMessage(error)}`,
        error
      )
    }
    if (!context) {
      throw new GSplatImageError(
        'GSPLAT_IMAGE_CONTEXT_UNAVAILABLE',
        'GSplatImage 无法创建 2D canvas context'
      )
    }

    let rgba: Uint8ClampedArray
    try {
      context.drawImage(source, 0, 0)
      rgba = context.getImageData(0, 0, width, height).data
    } catch (error) {
      throw new GSplatImageError(
        'GSPLAT_IMAGE_READBACK_FAILED',
        `GSplatImage canvas 像素读取失败: ${errorMessage(error)}`,
        error
      )
    }
    if (rgba.length !== width * height * 4) {
      throw new GSplatImageError(
        'GSPLAT_IMAGE_READBACK_FAILED',
        `GSplatImage RGBA 长度不匹配: actual=${rgba.length}, expected=${width * height * 4}`
      )
    }

    let totalSplats = 0
    for (let index = 3; index < rgba.length; index += 4) {
      if (rgba[index] > 0) totalSplats++
    }
    this.assertGeneration(operationGeneration)
    if (totalSplats === 0) {
      this.commitEmpty(operationGeneration)
      return null
    }

    const maxDim = Math.max(width, height)
    const splatSize = (1 / maxDim) * 1.1
    const adapter = this.runtime.graphicsAdapter
    let container: GSplatContainer | null = null
    try {
      try {
        const format = (this.formatValue ??= GSplatFormat.createSimpleFormat(adapter))
        container = new GSplatContainer(adapter, totalSplats, format)
      } catch (error) {
        throw new GSplatImageError(
          'GSPLAT_IMAGE_ALLOCATION_FAILED',
          `GSplatImage container 分配失败: ${errorMessage(error)}`,
          error
        )
      }
      this.stagedContainer = container
      this.assertGeneration(operationGeneration)

      const dimensions = container.textureDimensions
      const texelCount = dimensions.width * dimensions.height
      const centerData = new Float32Array(texelCount * 4)
      const colorData = new Uint16Array(texelCount * 4)
      const centers = container.centers
      let splatIndex = 0
      for (let py = 0; py < height; py++) {
        for (let px = 0; px < width; px++) {
          const pixelOffset = (py * width + px) * 4
          const alphaByte = rgba[pixelOffset + 3]
          if (alphaByte === 0) continue

          const x = px / maxDim - 0.5
          const y = 0
          const z = -(py / maxDim - 0.5)
          const centerOffset = splatIndex * 4
          centerData[centerOffset] = x
          centerData[centerOffset + 1] = y
          centerData[centerOffset + 2] = z
          centerData[centerOffset + 3] = splatSize
          colorData[centerOffset] = THREE.DataUtils.toHalfFloat(rgba[pixelOffset] / 255)
          colorData[centerOffset + 1] = THREE.DataUtils.toHalfFloat(rgba[pixelOffset + 1] / 255)
          colorData[centerOffset + 2] = THREE.DataUtils.toHalfFloat(rgba[pixelOffset + 2] / 255)
          colorData[centerOffset + 3] = THREE.DataUtils.toHalfFloat(alphaByte / 255)

          const sortingOffset = splatIndex * 3
          centers[sortingOffset] = x
          centers[sortingOffset + 1] = y
          centers[sortingOffset + 2] = z
          splatIndex++
        }
      }

      const centerTexture = container.getTexture('dataCenter')
      const colorTexture = container.getTexture('dataColor')
      if (!centerTexture || !colorTexture) {
        throw new GSplatImageError(
          'GSPLAT_IMAGE_ALLOCATION_FAILED',
          'GSplatImage simple format texture 不完整'
        )
      }
      try {
        adapter.updateTexture(centerTexture, centerData)
        adapter.updateTexture(colorTexture, colorData)
      } catch (error) {
        throw new GSplatImageError(
          'GSPLAT_IMAGE_UPLOAD_FAILED',
          `GSplatImage texture 上传失败: ${errorMessage(error)}`,
          error
        )
      }
      this.assertGeneration(operationGeneration)

      try {
        container.aabb.set(
          new THREE.Vector3(-0.5, -splatSize, -0.5),
          new THREE.Vector3(0.5, splatSize, 0.5)
        )
        container.update(totalSplats)
      } catch (error) {
        throw new GSplatImageError(
          'GSPLAT_IMAGE_UPDATE_FAILED',
          `GSplatImage container update 失败: ${errorMessage(error)}`,
          error
        )
      }
      this.assertGeneration(operationGeneration)

      const lease = container.acquire()
      container.destroy()
      container = null
      this.stagedContainer = null
      return this.commitResource(operationGeneration, lease)
    } finally {
      if (this.stagedContainer === container) this.stagedContainer = null
      container?.destroy()
    }
  }

  destroy(): void {
    if (this.disposed) return
    this.disposed = true
    this.generation++
    this.stagedContainer?.destroy()
    this.stagedContainer = null
    const handle = this.handleValue
    const stagedHandle = this.stagedHandle
    this.handleValue = null
    this.stagedHandle = null
    let firstError: unknown
    const disposeHandle = (target: GaussianSplatHandle | null) => {
      if (!target) return
      try {
        this.runtime.disposeAsset(target.id)
      } catch (error) {
        firstError ??= error
      }
    }
    disposeHandle(handle)
    if (stagedHandle !== handle) disposeHandle(stagedHandle)
    try {
      this.onDispose?.(this)
    } catch (error) {
      firstError ??= error
    }
    if (firstError) throw firstError
  }

  dispose(): void {
    this.destroy()
  }

  private commitResource(
    operationGeneration: number,
    resource: GSplatResourceLease
  ): GaussianSplatHandle {
    let lease: GSplatResourceLease | null = resource
    try {
      this.assertGeneration(operationGeneration)
      let handle: GaussianSplatHandle
      if (this.handleValue) {
        handle = this.runtime.replaceWithResource(this.handleValue.id, lease, {
          name: this.name,
        })
        lease = null
      } else {
        handle = this.runtime.createWithResource(
          Object.freeze({
            url: `procedural://gsplat-image/${this.id ?? 'generated'}`,
            name: this.name,
            ...(this.id ? { id: this.id } : {}),
          }),
          lease
        )
        lease = null
        this.stagedHandle = handle
        try {
          this.runtime.mount(handle)
        } catch (error) {
          try {
            this.runtime.disposeAsset(handle.id)
          } catch (cleanupError) {
            log.warn(`GSplatImage mount 回滚失败: ${errorMessage(cleanupError)}`)
          } finally {
            this.stagedHandle = null
          }
          throw error
        }
      }
      this.assertGeneration(operationGeneration)
      this.handleValue = handle
      this.stagedHandle = null
      return handle
    } catch (error) {
      lease?.dispose()
      throw this.toError(error, 'GSPLAT_IMAGE_COMMIT_FAILED', '提交 GSplat Resource 失败')
    }
  }

  private commitEmpty(operationGeneration: number): void {
    this.assertGeneration(operationGeneration)
    if (!this.handleValue) return

    const adapter = this.runtime.graphicsAdapter
    let container: GSplatContainer | null = null
    try {
      try {
        const format = (this.formatValue ??= GSplatFormat.createSimpleFormat(adapter))
        container = new GSplatContainer(adapter, 0, format)
      } catch (error) {
        throw new GSplatImageError(
          'GSPLAT_IMAGE_ALLOCATION_FAILED',
          `GSplatImage empty container 分配失败: ${errorMessage(error)}`,
          error
        )
      }
      this.stagedContainer = container
      this.assertGeneration(operationGeneration)
      try {
        container.aabb.makeEmpty()
        container.update(0)
      } catch (error) {
        throw new GSplatImageError(
          'GSPLAT_IMAGE_UPDATE_FAILED',
          `GSplatImage empty container update 失败: ${errorMessage(error)}`,
          error
        )
      }

      const lease = container.acquire()
      container.destroy()
      container = null
      this.stagedContainer = null
      this.commitResource(operationGeneration, lease)
    } finally {
      if (this.stagedContainer === container) this.stagedContainer = null
      container?.destroy()
    }
  }

  private getSourceKey(): GSplatImageSourceKey {
    const asset = this.imageAsset
    const texture = asset?.resource ?? null
    return Object.freeze({
      assetIdentity: asset?.id ?? asset,
      assetRevision: asset?.revision,
      textureIdentity: texture,
      textureRevision: texture?.revision,
    })
  }

  private assertActive(): void {
    if (this.disposed) {
      throw new GSplatImageError('GSPLAT_IMAGE_DISPOSED', 'GSplatImage 已销毁')
    }
  }

  private assertGeneration(operationGeneration: number): void {
    if (this.disposed || this.generation !== operationGeneration) {
      throw new GSplatImageError('GSPLAT_IMAGE_STALE', 'GSplatImage rebuild 已失效')
    }
  }

  private toError(error: unknown, code: GSplatImageErrorCode, prefix: string): GSplatImageError {
    if (error instanceof GSplatImageError) return error
    return new GSplatImageError(code, `${prefix}: ${errorMessage(error)}`, error)
  }
}
